import React, { useState, useMemo } from 'react'
import {
  Briefcase,
  DollarSign,
  HelpCircle,
  ChevronUp,
  ChevronDown,
  Lock,
  ShoppingCart,
  MapPin,
  Trophy
} from 'lucide-react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { MetricCard } from '../shared/MetricCard'
import { CAREERS, JOBS } from '../../constants/jobs'
import { UPGRADES } from '../../constants/upgrades'
import SKILLS from '../../constants/skills'
import { CareerPath, GamePlayData, Job, JobEnum, SkillEnum, UnlockRule, UpgradeEnum } from '../../types'
import {
  formatCurrency,
  isUnlockRuleMet,
  getUnworkedJobsCount,
  getTotalUnworkedJobsCount,
  isCareerPathUnlocked
} from '@/utils'
import { MIN_ENERGY_FOR_ACTIVITY_START } from '@/constants/game'
import { Tooltip } from '@nextui-org/react'
import { IncomeBreakdown } from '../shared/IncomeBreakdown'
import { getIncomeBreakdown } from '@/engine/equipment'
import { Tabs } from '../shared/Tabs'

export const JobsScreen: React.FC = () => {
  const { gameData, setGameData } = useGameplayContext()
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const { jobIncome, hourlyFees, netIncome, feeBreakdown } = getIncomeBreakdown(gameData)

  // Get all unlocked career paths
  const unlockedCareerPaths = useMemo(
    () => Object.values(CAREERS).filter((careerPath) => isCareerPathUnlocked(careerPath, gameData)),
    [gameData]
  )

  // Create tabs based on unlocked career paths
  const jobsTabs = useMemo(() => {
    return unlockedCareerPaths.map((career) => {
      const unworkedCount = getUnworkedJobsCount(career, gameData)
      return {
        value: career.key,
        label: career.name,
        badge:
          unworkedCount > 0 ? (
            <span className="bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
              {unworkedCount}
            </span>
          ) : undefined
      }
    })
  }, [unlockedCareerPaths, gameData])

  const handleTabChange = (value: string) => {
    setGameData({
      ...gameData,
      tabs: {
        ...gameData.tabs,
        jobs: value
      }
    })
  }

  // Set default active tab when paths are loaded or changed
  useMemo(() => {
    if (unlockedCareerPaths.length > 0 && !gameData.tabs.jobs) {
      handleTabChange(unlockedCareerPaths[0].key)
    }
  }, [unlockedCareerPaths, gameData.tabs.jobs])

  const handleWorkToggle = () => {
    if (gameData.currentActivity === 'work') {
      setGameData({ ...gameData, currentActivity: 'rest' })
    } else if (gameData.currentJob && gameData.currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START) {
      setGameData({ ...gameData, currentActivity: 'work' })
    }
  }

  const tooltipContent = (
    <div className="max-w-md space-y-1 p-2">
      <p className="pt-2 pb-2 border-b border-gray-300">
        💵 Here is the place to earn money. You can start and stop your current work. First you need to select a job.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        ⚡️ Work requires energy so you need to make hard decisions (as in real life) if you want to spend time working
        or surfing.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        🏆 Better paid jobs usually require certain amount of hours worked in previous jobs and eventually some other
        requirements.
      </p>
      <p className="pt-2 pb-2">
        🏄‍♂️ As you progress through the game and travel to new locations, more lucrative career paths await you so you
        can spend less time working and more surfing
      </p>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h2 className="text-3xl font-bold text-white drop-shadow-md">💵 Jobs</h2>
        <Tooltip
          content={tooltipContent}
          isOpen={isTooltipOpen}
          onOpenChange={(open) => setIsTooltipOpen(open)}
          className="bg-white backdrop-blur-sm text-sm"
          placement="bottom"
        >
          <button
            className="text-white hover:text-blue-300 transition-colors"
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <HelpCircle className="w-6 h-6" />
          </button>
        </Tooltip>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard emoji="💰" label="Money" value={`$${formatCurrency(gameData.currencies.money.amount)}`} />
        <MetricCard
          key="energy"
          emoji="⚡"
          label="Energy"
          value={formatCurrency(gameData.currencies.energy.amount)}
          max={gameData.currencies.energy.max}
        />
        <MetricCard
          emoji="💰"
          label="Income"
          value={
            <span className={netIncome < 0 ? 'text-red-500' : ''}>
              {netIncome >= 0 ? '' : '-'}${formatCurrency(Math.abs(netIncome))}/hour
            </span>
          }
          tooltip={
            <IncomeBreakdown
              jobIncome={jobIncome}
              hourlyFees={hourlyFees}
              netIncome={netIncome}
              feeBreakdown={feeBreakdown}
            />
          }
        />
        <MetricCard
          emoji="👔"
          label="Current Job"
          value={gameData.currentJob ? JOBS[gameData.currentJob as JobEnum]?.name : 'Unemployed'}
        />
      </div>

      <div className="flex mb-6">
        <Tooltip
          content={
            !gameData.currentJob
              ? 'You need a job first'
              : gameData.currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START
              ? `Need ${formatCurrency(MIN_ENERGY_FOR_ACTIVITY_START - gameData.currencies.energy.amount)} more energy`
              : ''
          }
          className="bg-white bg-opacity-80 backdrop-blur-sm text-sm"
          placement="bottom"
          isDisabled={
            gameData.currentJob &&
            (gameData.currentActivity === 'work' || gameData.currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START)
          }
        >
          <button
            className={`bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg text-xl transition-colors inline-flex items-center ${
              (!gameData.currentJob || gameData.currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START) &&
              'opacity-50 cursor-not-allowed'
            }`}
            onClick={handleWorkToggle}
            disabled={
              !gameData.currentJob &&
              gameData.currentActivity !== 'work' &&
              gameData.currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START
            }
          >
            {gameData.currentActivity === 'work' ? (
              <>
                <Briefcase className="w-6 h-6 mr-2" />
                Stop Working
              </>
            ) : (
              <>
                <Briefcase className="w-6 h-6 mr-2" />
                Start Working
              </>
            )}
          </button>
        </Tooltip>
      </div>

      {unlockedCareerPaths.length > 0 && (
        <div className="space-y-6">
          <Tabs tabs={jobsTabs} activeTab={gameData.tabs.jobs || ''} onChange={handleTabChange} />

          {unlockedCareerPaths.map(
            (careerPath) =>
              careerPath.key === gameData.tabs.jobs && (
                <div key={careerPath.key}>
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-xl font-semibold text-white">{careerPath.name}</h3>
                    <p className="text-sm text-yellow-200">{careerPath.description}</p>
                  </div>
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                    <div className="space-y-4">
                      {careerPath.jobs.map((jobKey, index) => {
                        const job = JOBS[jobKey as JobEnum]
                        const isJobUnlocked = job.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))
                        const isPreviousJobUnlocked =
                          index === 0 ||
                          JOBS[careerPath.jobs[index - 1] as JobEnum].unlockRules.every((rule) =>
                            isUnlockRuleMet(rule, gameData)
                          )

                        if (isJobUnlocked || (isPreviousJobUnlocked && !isJobUnlocked)) {
                          return (
                            <JobCard
                              key={jobKey}
                              job={job}
                              isLast={index === careerPath.jobs.length - 1}
                              isActive={gameData.currentJob === jobKey}
                              isUnlocked={isJobUnlocked}
                            />
                          )
                        }
                        return null
                      })}
                    </div>
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </div>
  )
}

const JobCard: React.FC<{
  job: Job
  isLast: boolean
  isUnlocked: boolean
  isActive: boolean
}> = ({ job, isLast, isUnlocked, isActive }) => {
  const [isExpanded, setIsExpanded] = useState(isLast || isActive)
  const { gameData, setGameData } = useGameplayContext()

  const isCurrentJob = gameData.currentJob === job.key
  const isWorking = gameData.currentActivity === 'work'

  const handleJobChange = () => {
    setGameData({ ...gameData, currentJob: job.key })
  }

  if (!isUnlocked) {
    return (
      <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center">
          <Lock className="w-6 h-6 mr-2 text-yellow-400" />
          <span className="text-white font-semibold">Unknown Job</span>
        </div>
        <div className="text-sm">
          {job.unlockRules.map((rule, index) => (
            <div key={index} className="flex items-center ml-4">
              {getUnlockIcon(rule.category)}
              <span className={isUnlockRuleMet(rule, gameData) ? 'text-green-400' : 'text-yellow-200'}>
                {formatUnlockRule(rule)}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-gray-700 bg-opacity-50 rounded-lg p-4 ${isCurrentJob ? 'border-2 border-green-400' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Briefcase className="w-6 h-6 mr-2 text-blue-400" />
          <span className="text-white font-semibold">{job.name}</span>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center">
            <DollarSign className="w-5 h-5 mr-1 text-green-400" />
            <span className="text-white">{job.income}/hour</span>
            {job.respectIncome && (
              <>
                <span className="mx-2 text-gray-400">•</span>
                <span className="text-white flex items-center">
                  <span className="mr-1">👏</span>
                  {job.respectIncome}/hour
                </span>
              </>
            )}
            {!isLast && (
              <button className="ml-2 text-white" onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            )}
          </div>
          {isExpanded && !isCurrentJob && (
            <button
              className="mt-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors bg-blue-500 text-white hover:bg-blue-600 flex items-center justify-center"
              onClick={handleJobChange}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              <span>Select Job</span>
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="mt-2">
          <p className="text-white text-sm">{job.description}</p>
          <div className="text-sm text-yellow-200 mt-1">
            Hours worked: {formatCurrency(gameData.jobs[job.key] || 0)}
          </div>
        </div>
      )}
    </div>
  )
}

const formatUnlockRule = (rule: UnlockRule): string => {
  switch (rule.category) {
    case 'level':
      return `Reach ${SKILLS[rule.key as SkillEnum]?.name || (rule.key === 'skill' ? 'Surf' : rule.key)} level ${
        rule.value
      }`
    case 'currency':
      return `Have ${rule.value} ${rule.key}` // Assuming currency keys are already human-readable
    case 'job':
      return `Work ${rule.value} hours as ${JOBS[rule.key as JobEnum]?.name || rule.key}`
    case 'location':
      return `Be in ${rule.value}` // Assuming location values are already human-readable
    case 'purchase':
      return `Purchase ${UPGRADES[rule.key as UpgradeEnum]?.name || rule.key}`
    default:
      return ''
  }
}

const getUnlockIcon = (category: string) => {
  switch (category) {
    case 'job':
      return <Briefcase className="w-4 h-4 mr-2 text-blue-400" />
    case 'purchase':
      return <ShoppingCart className="w-4 h-4 mr-2 text-green-400" />
    case 'location':
      return <MapPin className="w-4 h-4 mr-2 text-red-400" />
    case 'level':
      return <Trophy className="w-4 h-4 mr-2 text-purple-400" />
    default:
      return <HelpCircle className="w-4 h-4 mr-2 text-gray-400" />
  }
}

export default JobsScreen
